import type { FieldError, FieldErrors } from 'react-hook-form';

const RHF_METADATA_KEYS = new Set(['ref', 'type', 'types']);

function isLeafFieldError(node: unknown): node is FieldError {
  if (!node || typeof node !== 'object') return false;
  const o = node as Record<string, unknown>;
  return typeof o.message === 'string' && o.message.length > 0;
}

/**
 * Collects user-facing messages from react-hook-form FieldErrors (nested-safe)
 * and the first dot-notation field path for setFocus.
 */
export function summarizeRhfFieldErrors(
  errors: FieldErrors,
  options?: { maxMessages?: number },
): { summary: string; firstFieldPath?: string } {
  const maxMessages = options?.maxMessages ?? 4;
  const messages: string[] = [];
  let firstFieldPath: string | undefined;

  function walk(node: unknown, path: string[]): void {
    if (messages.length >= maxMessages || node == null) return;
    if (typeof node !== 'object') return;

    if (isLeafFieldError(node)) {
      if (!firstFieldPath && path.length > 0) {
        firstFieldPath = path.join('.');
      }
      if (messages.length < maxMessages) {
        messages.push(String(node.message));
      }
      return;
    }

    const record = node as Record<string, unknown>;
    for (const key of Object.keys(record)) {
      if (key !== 'root' && !RHF_METADATA_KEYS.has(key)) {
        walk(record[key], [...path, key]);
      }
    }
  }

  walk(errors, []);

  return {
    summary: messages.join(' '),
    firstFieldPath,
  };
}
